import { forwardRef, ReactElement, ReactNode, RefObject, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Timestamp } from 'firebase/firestore';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveCircleOutlinedIcon from '@mui/icons-material/RemoveCircleOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';

import dayjs from 'dayjs';

import { ActionProps } from '../../contextProviders/AppProvider';
import { useAutoSave } from '../../stateHooks/useAutoSave';
import Competition, {
  ActivePerHole,
  ActiveRotation,
  CompAmplifier,
  CompAggregation,
  CompEntry,
  CompField,
  CompFormat,
  CompHandicap,
  CompHoleCapType,
  CompNetHandicapBasis,
  CompScoreAmplifier,
  CompScoring,
  CompScoringAmplifierCondition,
  CompTiebreaker,
  GroupScoringConfig,
  HoleGroupScore,
  ScorecardConfig,
  ScorecardExtraRow,
  ScorecardExtraRowType,
  ScoreFilter,
  ScoreWindow,
  ALL_ACTIVE_ROTATIONS,
  COMP_AGGREGATIONS,
  COMP_ENTRIES,
  COMP_FIELDS,
  COMP_FORMATS,
  COMP_HANDICAPS,
  COMP_HOLE_CAP_TYPES,
  COMP_NET_HANDICAP_BASIS,
  COMP_POINTS_TABLES,
  COMP_SCORE_AMPLIFIERS,
  COMP_SCORE_AMPLIFIER_CONDITIONS,
  COMP_SCORINGS,
  COMP_SCORING_METHODS,
  COMP_TIEBREAKERS,
  SCORECARD_EXTRA_ROW_TYPES,
  SCORE_FILTER_BASIS,
  SCORE_FILTER_TYPES,
  COMPETITION_PRESETS,
  DEFAULT_ACTIVE_ROTATION,
  DEFAULT_MATCH_GAME_SCORES,
  DEFAULT_MATCH_HOLE_SCORES,
  MATCH_TOTAL_BASES,
  MatchOutcomeScores,
  ONE_SCORE_FORMATS,
  inferCompetitionParams,
} from '../../types/Competition';
import { AttendeeOption, ATTENDEE_OPTIONS } from '../../types/Event';
import { EventAggregateCompetition } from '../../types/EventAggregateCompetition';
import EventDay from '../../types/EventDay';
import Grouping, { GROUPING_TYPES } from '../../types/Grouping';
import League from '../../types/League';
import { LeagueCompetition } from '../../types/LeagueCompetition';
import { resolveActiveSeats } from '../../utils/activePerHole';
import { vsUnitNoun } from '../../utils/competitionUtils';
import { eventPartnershipSize, isPartnershipEvent, type PartnershipEventLike } from '../../utils/eventTeamUtils';
import { isPlayerGameChoiceOffered } from '../../utils/gameParticipation';
import { HANDICAP_LABELS, resolveHandicapPctForRank } from '../../utils/handicapUtils';
import { defaultUnitSource, resolveAllowanceUnitSize, resolveCompetingUnit, SCORING_UNIT_SOURCES, type ScoringUnitSource } from '../../utils/scoringTypeUtils';
import { formatEnumLabel } from '../../utils/utils';
import {
  contiguousSeatPattern,
  dayIndicesForCompetition,
  formatSeatPattern,
  holePickerChoices,
  seatPatternOptions,
  seatPatternsEqual,
  selectedDayIndices,
} from '../../utils/utilsEvents';
import IndexBracketListEditor from '../common/IndexBracketListEditor';
import EventDayHolesToggle from '../event/EventDayHolesToggle';

import MissedDayAllowanceEditor from './MissedDayAllowanceEditor';

// ── Exported section type + ref interface ────────────────────────────────────

export type CompEditorSection = 'who' | 'scoring' | 'handicap' | 'rules' | 'league' | 'combine';

export interface CompetitionEditorRef {
  focusSection: (section: CompEditorSection) => void;
  flushChanges: () => Promise<void>;
}

// ── Module-level constants ────────────────────────────────────────────────────

const ALL_COMP_NET_HANDICAP_BASES = Object.values(COMP_NET_HANDICAP_BASIS) as CompNetHandicapBasis[];
// "Set by hole" always renders this many boxes, since no numHoles context reaches this editor -
// see the handleHoleBasedToggle comment below.
const GROUP_SCORING_HOLE_COUNT = 18;

const ACTIVE_ROTATION_LABELS: Record<ActiveRotation, string> = {
  EVERY_COMBINATION_IN_ORDER: 'Rotate by seat',
};
// GROUPING/EXPLICIT_MATCHUP exist on the CompField type for the (still-unwired) comparisonPool
// mechanism, but nothing in the results engine reads them yet - excluded here so they can't be
// selected until that UI actually lands (results-engine plan, Step 4 phase 4.7). PHYSICAL_GROUP
// is real now (classifyAndRerankByPhysicalGroup, resultDispatch.ts) for Stroke/Windowed
// Stroke/Count/Vegas — Skins doesn't support it yet (same reason it needed its own INDEX
// treatment: its per-hole winner must be decided within the group, not after), confirmed inert
// (falls through to ALL) rather than wrong, so it's not blocked here — same "don't disallow a
// harmless no-op combo" rule as Match play's Vs row — a caption at the Vs picker itself says so.
const UNWIRED_COMP_FIELDS = new Set<CompField>([COMP_FIELDS.GROUPING, COMP_FIELDS.EXPLICIT_MATCHUP]);
const ALL_COMP_FIELDS = (Object.values(COMP_FIELDS) as CompField[]).filter((v) => !UNWIRED_COMP_FIELDS.has(v));
const ALL_COMP_ENTRIES = Object.values(COMP_ENTRIES) as CompEntry[];
const ALL_COMP_FORMATS = Object.values(COMP_FORMATS) as CompFormat[];
const ALL_COMP_SCORINGS = Object.values(COMP_SCORINGS) as CompScoring[];
const ALL_COMP_SCORE_AMPLIFIERS = Object.values(COMP_SCORE_AMPLIFIERS) as CompScoreAmplifier[];
const ALL_COMP_SCORE_AMPLIFIER_CONDITIONS = Object.values(COMP_SCORE_AMPLIFIER_CONDITIONS) as CompScoringAmplifierCondition[];
const ALL_COMP_TIEBREAKERS = Object.values(COMP_TIEBREAKERS) as CompTiebreaker[];
const ALL_SCORECARD_EXTRA_ROW_TYPES = Object.values(SCORECARD_EXTRA_ROW_TYPES) as ScorecardExtraRowType[];
const MATCH_SCORINGS = new Set<CompScoring>([COMP_SCORINGS.MATCH, COMP_SCORINGS.MATCH_FULL_ROUND]);
const SPECIAL_EVENT_SCORINGS = new Set<CompScoring>([COMP_SCORINGS.CLOSEST_TO_PIN, COMP_SCORINGS.LONG_DRIVE]);

// ── Simplified Format / Scores / Vs pickers ──────────────────────────────────
// These are the common-case front door onto the same entry/format/field the raw dropdowns below
// (revealed by picking "Custom") still edit directly — no second data model, just a curated subset
// of each enum with friendlier labels. A stored value outside that curated subset (e.g. entry=TEAM,
// format=RABBIT, a deprecated/unwired field value) always derives to 'custom' so nothing existing
// is ever misrepresented — it just means Custom is where you go to see/change it.

type SimpleFormat = 'individual' | 'partner' | 'foursome' | 'team' | 'custom';
type SimpleVs = 'field' | 'flight' | 'division' | 'physicalGroup' | 'custom';
type SimpleScores = CompFormat | 'custom';

const FORMAT_MODE_LABELS: Record<Exclude<SimpleFormat, 'custom'>, string> = {
  individual: 'Individual', partner: 'Partners', foursome: 'Foursome', team: 'Team',
};
const VS_MODE_LABELS: Record<Exclude<SimpleVs, 'custom'>, string> = {
  field: 'Field', flight: 'Flight', division: 'Division', physicalGroup: 'Pairing Group',
};
// Scramble/Shamble/Bestball/Alt shot/Chapman/Total cover the common "how do 2+ scores combine"
// cases; Rabbit and anything unset fall through to Custom.
const SIMPLE_SCORES_FORMATS: CompFormat[] = [
  COMP_FORMATS.BESTBALL, COMP_FORMATS.TOTAL, COMP_FORMATS.ALTERNATE_SHOT,
  COMP_FORMATS.CHAPMAN, COMP_FORMATS.SCRAMBLE, COMP_FORMATS.SHAMBLE,
];
const SCORES_MODE_LABELS: Record<CompFormat, string> = {
  [COMP_FORMATS.BESTBALL]: 'Best ball',
  [COMP_FORMATS.TOTAL]: 'Total',
  [COMP_FORMATS.ALTERNATE_SHOT]: 'Alternate shot',
  [COMP_FORMATS.CHAPMAN]: 'Chapman',
  [COMP_FORMATS.SCRAMBLE]: 'Scramble',
  [COMP_FORMATS.SHAMBLE]: 'Shamble',
  [COMP_FORMATS.RABBIT]: 'Rabbit',
};

function entryToFormatMode(entry: CompEntry | undefined): SimpleFormat {
  switch (entry) {
    case COMP_ENTRIES.SINGLE: return 'individual';
    case COMP_ENTRIES.PAIR: return 'partner';
    case COMP_ENTRIES.FOURSOME: return 'foursome';
    case COMP_ENTRIES.GROUP: return 'team';
    default: return 'custom';
  }
}
function formatModeToEntry(mode: Exclude<SimpleFormat, 'custom'>): CompEntry {
  if (mode === 'individual') return COMP_ENTRIES.SINGLE;
  if (mode === 'partner') return COMP_ENTRIES.PAIR;
  if (mode === 'foursome') return COMP_ENTRIES.FOURSOME;
  return COMP_ENTRIES.GROUP;
}

function fieldToVsMode(field: CompField | undefined): SimpleVs {
  switch (field) {
    case undefined:
    case COMP_FIELDS.ALL: return 'field';
    case COMP_FIELDS.FLIGHT: return 'flight';
    case COMP_FIELDS.DIVISION: return 'division';
    case COMP_FIELDS.PHYSICAL_GROUP: return 'physicalGroup';
    default: return 'custom';
  }
}
function vsModeToField(mode: Exclude<SimpleVs, 'custom'>): CompField {
  if (mode === 'flight') return COMP_FIELDS.FLIGHT;
  if (mode === 'division') return COMP_FIELDS.DIVISION;
  if (mode === 'physicalGroup') return COMP_FIELDS.PHYSICAL_GROUP;
  return COMP_FIELDS.ALL;
}

function formatToScoresMode(format: CompFormat | undefined): SimpleScores {
  return format && SIMPLE_SCORES_FORMATS.includes(format) ? format : 'custom';
}

// Match Play, Stroke, Stableford, Skins, Nassau cover the vast majority of real games played —
// Vegas, Count, and the rest fall through to Custom, same "curated subset, nothing lost" rule.
type SimpleGame = 'match' | 'stroke' | 'stableford' | 'skins' | 'nassau' | 'custom';

const GAME_MODE_LABELS: Record<Exclude<SimpleGame, 'custom'>, string> = {
  match: 'Match Play', stroke: 'Stroke', stableford: 'Stableford', skins: 'Skins', nassau: 'Nassau',
};

function scoringToGameMode(scoring: CompScoring | undefined): SimpleGame {
  switch (scoring) {
    case COMP_SCORINGS.MATCH:
    case COMP_SCORINGS.MATCH_FULL_ROUND: return 'match';
    case COMP_SCORINGS.STROKE: return 'stroke';
    case COMP_SCORINGS.STABLEFORD: return 'stableford';
    case COMP_SCORINGS.SKINS_BASIC:
    case COMP_SCORINGS.SKINS_CANADIAN:
    case COMP_SCORINGS.SKINS_SILK: return 'skins';
    case COMP_SCORINGS.NASSAU:
    case COMP_SCORINGS.NASSAU_9_HOLES: return 'nassau';
    default: return 'custom';
  }
}
function gameModeToScoring(mode: Exclude<SimpleGame, 'custom'>): CompScoring {
  if (mode === 'match') return COMP_SCORINGS.MATCH;
  if (mode === 'stroke') return COMP_SCORINGS.STROKE;
  if (mode === 'stableford') return COMP_SCORINGS.STABLEFORD;
  if (mode === 'nassau') return COMP_SCORINGS.NASSAU;
  return COMP_SCORINGS.SKINS_BASIC;
}

const TIEBREAKER_LABELS: Record<CompTiebreaker, string> = {
  [COMP_TIEBREAKERS.BACK9]: 'Back 9',
  [COMP_TIEBREAKERS.FRONT9]: 'Front 9',
  [COMP_TIEBREAKERS.LAST9]: 'Last 9',
  [COMP_TIEBREAKERS.LAST6]: 'Last 6',
  [COMP_TIEBREAKERS.LAST3]: 'Last 3',
  [COMP_TIEBREAKERS.LAST1]: 'Last hole',
  [COMP_TIEBREAKERS.LOW_HANDICAP]: `Low ${HANDICAP_LABELS.INDEX_SHORT}`,
  [COMP_TIEBREAKERS.HIGH_HANDICAP]: `High ${HANDICAP_LABELS.INDEX_SHORT}`,
  [COMP_TIEBREAKERS.PLAYOFF]: 'Playoff',
  [COMP_TIEBREAKERS.CUSTOM]: 'Custom',
  [COMP_TIEBREAKERS.NONE]: 'None (ties stand)',
  [COMP_TIEBREAKERS.USGA_MATCHING_SCORECARDS]: 'USGA',
};

const SCORE_FILTER_PRESETS: { label: string; filter: ScoreFilter }[] = [
  { label: 'Eagles or better',  filter: { type: SCORE_FILTER_TYPES.MAX,   basis: SCORE_FILTER_BASIS.VS_PAR,   value: -2 } },
  { label: 'Birdies',           filter: { type: SCORE_FILTER_TYPES.EXACT, basis: SCORE_FILTER_BASIS.VS_PAR,   value: -1 } },
  { label: 'Net par or better', filter: { type: SCORE_FILTER_TYPES.MAX,   basis: SCORE_FILTER_BASIS.VS_PAR,   value:  0 } },
  { label: 'Bogeys',            filter: { type: SCORE_FILTER_TYPES.EXACT, basis: SCORE_FILTER_BASIS.VS_PAR,   value:  1 } },
  { label: 'Deuces',            filter: { type: SCORE_FILTER_TYPES.EXACT, basis: SCORE_FILTER_BASIS.ABSOLUTE, value:  2 } },
  { label: 'Treys',             filter: { type: SCORE_FILTER_TYPES.EXACT, basis: SCORE_FILTER_BASIS.ABSOLUTE, value:  3 } },
];

function scoreFilterKey(f: ScoreFilter): string { return `${f.type}|${f.basis}|${f.value}`; }


function computeDayLabels(days: EventDay[]): string[] {
  const parsed = days.map(d => d.datetime instanceof Timestamp ? dayjs(d.datetime.toDate()) : null);
  const dateStrs = parsed.map(d => d?.format('YYYY-MM-DD') ?? '');
  const hasDuplicate = dateStrs.some((s, i) => s && dateStrs.indexOf(s) !== i);
  return parsed.map((d, i) => d ? d.format(hasDuplicate ? 'M/D h:mm A' : 'M/D/YY') : `Day ${i + 1}`);
}

// ── CollapsibleSection ────────────────────────────────────────────────────────

interface CollapsibleSectionProps {
  sectionRef: RefObject<HTMLDivElement | null>;
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
}

function CollapsibleSection({ sectionRef, title, isOpen, onToggle, children }: CollapsibleSectionProps): ReactElement {
  return (
    <Paper ref={sectionRef} variant="outlined" sx={{ overflow: 'hidden', py: 0, my: 0 }}>
      <Button
        fullWidth
        variant="text"
        onClick={onToggle}
        endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ justifyContent: 'space-between', px: 2, textTransform: 'none' }}
      >
        <Typography variant="labelLarge">{title}</Typography>
      </Button>
      <Collapse in={isOpen}>
        <Stack spacing={2} sx={{ pb: 2, px: 2 }}>
          {children}
        </Stack>
      </Collapse>
    </Paper>
  );
}

// ── HandicapPctByUnitSizeEditor ─────────────────────────────────────────────────

interface HandicapPctByUnitSizeEditorProps {
  competition: Competition;
  onChange: (value: Record<number, number[]>) => void;
}

// Rank-weighted handicap allowance table for a team game: one row per unit size from 1 to the
// game's own competing unit size (resolveAllowanceUnitSize — 4 for a foursome, so a group short a
// player has its own row), one percentage per rank within the unit (best/lowest course handicap =
// rank 1, leftmost). Every cell shows exactly what the engine uses — resolveHandicapPctForRank,
// the format's USGA default until edited — and the engine never reads any other row, so no
// allowance in play is ever hidden. Editing a cell saves its whole row; Reset drops the row back
// to the default.
function HandicapPctByUnitSizeEditor({ competition, onChange }: HandicapPctByUnitSizeEditorProps): ReactElement {
  const value = competition.handicapPctByUnitSize;
  const sizes = Array.from({ length: resolveAllowanceUnitSize(competition) }, (_, i) => i + 1);

  const rowFor = (unitSize: number): number[] =>
    Array.from({ length: unitSize }, (_, i) => resolveHandicapPctForRank(competition, unitSize, i + 1));

  const setRow = (unitSize: number, row: number[]) => {
    onChange({ ...value, [unitSize]: row });
  };

  const resetRow = (unitSize: number) => {
    const next = { ...value };
    delete next[unitSize];
    onChange(next);
  };

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">Handicap allowance % by players in the unit (best → worst rank)</Typography>
      {sizes.map((unitSize) => {
        const row = rowFor(unitSize);
        return (
          <Stack key={unitSize} direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ minWidth: '5em' }}>{unitSize === 1 ? '1 player' : `${unitSize} players`}</Typography>
            {row.map((pct, rank) => (
              <TextField
                key={rank}
                label={`Rank ${rank + 1}`}
                type="number"
                size="small"
                slotProps={{ htmlInput: { min: 0, max: 100 } }}
                sx={{ width: '6em' }}
                value={pct}
                onChange={({ target }) => {
                  const next = [...row];
                  next[rank] = target.value ? parseFloat(target.value) : 0;
                  setRow(unitSize, next);
                }}
              />
            ))}
            {value?.[unitSize] && (
              <IconButton size="small" onClick={() => resetRow(unitSize)} aria-label={`Reset the ${unitSize}-player allowance to the default`}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

// ── MatchOutcomeScoreFields ──────────────────────────────────────────────────

interface MatchOutcomeScoreFieldsProps {
  label: string;
  value: MatchOutcomeScores | undefined;
  defaults: MatchOutcomeScores;
  onChange: (value: MatchOutcomeScores) => void;
}

// Win/Halve/Loss point-value trio shared by both "Hole scores" (always active for MATCH_WINS
// scoring — decides who wins each hole, and therefore the match) and "Game scores" (only read when
// matchTotalBasis is GAME — converts the match's own WIN/HALVED/LOSS outcome into totalScore).
// Same three fields, same defaults, same shape either way — one component instead of two
// hand-written copies.
function MatchOutcomeScoreFields({ label, value, defaults, onChange }: MatchOutcomeScoreFieldsProps): ReactElement {
  const current = value ?? defaults;
  const setKey = (key: keyof MatchOutcomeScores, raw: string) => {
    onChange({ ...current, [key]: raw ? parseFloat(raw) : 0 });
  };
  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Stack direction="row" spacing={2}>
        <TextField
          label="Win" type="number" size="small" sx={{ width: '6rem' }}
          slotProps={{ htmlInput: { step: 0.5 } }}
          value={current.win}
          onChange={({ target }) => setKey('win', target.value)}
        />
        <TextField
          label="Halve" type="number" size="small" sx={{ width: '6rem' }}
          slotProps={{ htmlInput: { step: 0.5 } }}
          value={current.halve}
          onChange={({ target }) => setKey('halve', target.value)}
        />
        <TextField
          label="Loss" type="number" size="small" sx={{ width: '6rem' }}
          slotProps={{ htmlInput: { step: 0.5 } }}
          value={current.loss}
          onChange={({ target }) => setKey('loss', target.value)}
        />
      </Stack>
    </Stack>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface CompetitionEditorProps {
  competition: Competition;
  /** The parent event — decides whether "Event partnerships" is offered as a unit source
   *  (isPartnershipEvent), the default source a new multi-player game is stamped with
   *  (defaultUnitSource), and the tee-sheet group size the seat-pattern options are built for.
   *  Not passed by the standalone god-only edit pages; the unit controls then assume a plain
   *  tee-sheet event with foursomes. */
  event?: PartnershipEventLike & { teeSheetGroupSize?: number };
  eventDays?: EventDay[];
  groupings?: Grouping[] | null;
  league?: League | null;
  /** Other competitions in this event — used only to detect a handicap mismatch against an
   *  already-linked EventAggregateCompetition's other members (see the "Combine" section). */
  siblingCompetitions?: Competition[];
  /** Candidates for the "Combine" section's picker — Event.eventAggregateCompetitions. */
  eventAggregateCompetitions?: EventAggregateCompetition[];
  /** The parent Event's attendeeOptions — gates the "include followers" checkbox, which only
   *  makes sense when the event has opted into ATTENDEE_OPTIONS.FOLLOW_FIELD. Not passed by the
   *  standalone god-only edit pages (they don't fetch the parent event), so the checkbox simply
   *  doesn't show there for now. */
  attendeeOptions?: AttendeeOption[];
  isNew?: boolean;
  onChange?: (competition: Partial<Competition>) => void;
  handleCreate?: (competition: Competition) => void;
  handleDelete?: (competition: Competition) => void;
  updateCompetition?: (competition: Partial<Competition>) => Promise<void>;
  onActionsChange?: (pageActions: ActionProps[] | null, godActions: ReactElement | null) => void;
}

// ── Main component ────────────────────────────────────────────────────────────

const CompetitionEditor = forwardRef<CompetitionEditorRef, CompetitionEditorProps>(function CompetitionEditor({
  competition: initialCompetition,
  event,
  eventDays,
  groupings,
  league,
  siblingCompetitions,
  eventAggregateCompetitions,
  attendeeOptions,
  isNew = false,
  onChange,
  handleCreate,
  handleDelete,
  updateCompetition,
  onActionsChange,
}, ref) {
  const [competition, setCompetition] = useState<Competition>(initialCompetition);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  // Holes are stored by position in the round; the picker labels each with its real hole number
  // for the golf days this game is played on (10–18 on back-nine days).
  const holeChoices = useMemo(() => {
    const days = eventDays ?? [];
    return holePickerChoices(dayIndicesForCompetition(competition, days).map((i) => days[i]).filter((d): d is EventDay => !!d));
  }, [competition, eventDays]);
  const holeLabelByPosition = useMemo(() => new Map(holeChoices.map((c) => [c.position, c.holeNumber])), [holeChoices]);
  const [openSections, setOpenSections] = useState<Set<CompEditorSection>>(() => new Set<CompEditorSection>());
  // Explicit "Custom" overrides for the simplified Format/Scores/Vs pickers — see the module
  // comment above entryToFormatMode. A stored value outside the curated subset already derives
  // to 'custom' on its own; these only matter for "I clicked Custom to look, but the underlying
  // value still happens to match a Tier-1 option" (e.g. entry is SINGLE and you tap Custom to
  // peek at the raw Entry dropdown) — without this the picker would snap straight back.
  const [formatCustom, setFormatCustom] = useState(false);
  const [scoresCustom, setScoresCustom] = useState(false);
  const [vsCustom, setVsCustom] = useState(false);
  const [gameCustom, setGameCustom] = useState(false);

  const whoRef = useRef<HTMLDivElement>(null);
  const scoringRef = useRef<HTMLDivElement>(null);
  const handicapRef = useRef<HTMLDivElement>(null);
  const rulesRef = useRef<HTMLDivElement>(null);
  const leagueRef = useRef<HTMLDivElement>(null);
  const combineRef = useRef<HTMLDivElement>(null);

  const { queueChange, flushChanges } = useAutoSave<Competition>({
    updateFn: updateCompetition,
    itemId: competition.itemId,
    enabled: !isNew && !!updateCompetition,
  });

  useImperativeHandle(ref, () => ({
    focusSection: (section: CompEditorSection) => {
      setOpenSections(prev => new Set([...prev, section]));
      const refs: Record<CompEditorSection, RefObject<HTMLDivElement | null>> = {
        who: whoRef, scoring: scoringRef, handicap: handicapRef, rules: rulesRef, league: leagueRef, combine: combineRef,
      };
      setTimeout(() => refs[section].current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    },
    flushChanges,
  }), [flushChanges]);

  const toggleSection = (section: CompEditorSection) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section); else next.add(section);
      return next;
    });
  };

  // Changing a game's format or team size only changes that game: each game reads the scores of its
  // own scoring type, so no other game on the day is affected (docs/scoring-types-plan.md).
  const handleFieldChange = <K extends keyof Competition>(field: K, value: Competition[K]) => {
    setCompetition((prev) => ({ ...prev, [field]: value }));
    queueChange(field, value);
    onChange?.({ [field]: value });
  };

  const handleScoringChange = (scoring: CompScoring | undefined) => {
    const inferred = scoring ? inferCompetitionParams(scoring) : {};
    const changes: Partial<Competition> = { scoring, ...inferred };
    // inferCompetitionParams deliberately omits `entry` — it's shared with the legacy-data
    // backfill trigger, which must never overwrite an existing competition's entry. But for a
    // brand-new competition (nothing saved yet to protect), switching Scoring directly to a
    // preset like Match Play should still apply that preset's own entry default (e.g. PAIR) —
    // otherwise picking Match Play here silently leaves whatever entry an earlier, unrelated
    // preset selection left behind (e.g. SINGLE from Stroke Play).
    if (isNew && scoring) {
      const presetEntry = COMPETITION_PRESETS[scoring]?.defaults.entry;
      if (presetEntry) changes.entry = presetEntry;
    }
    setCompetition((prev) => ({ ...prev, ...changes }));
    for (const [k, v] of Object.entries(changes)) {
      queueChange(k as keyof Competition, v as Competition[keyof Competition]);
    }
    onChange?.(changes);
  };

  // Day selection is stored twice — dayIndices and its stable dayIds equivalent, which wins whenever
  // it's set (competitionAppliesToDay). Both are written together, so a later change can never be
  // shadowed by dayIds left over from an earlier selection. undefined = all days. dayIds stays unset
  // if a selected day has no itemId yet, same as the server's deriveCompetitionDayIds.
  const handleDaysChange = (dayIndices: number[] | undefined) => {
    const dayIds = dayIndices?.map((i) => eventDays?.[i]?.itemId);
    const changes: Partial<Competition> = {
      dayIndices,
      dayIds: dayIds?.every((id): id is string => !!id) ? dayIds : undefined,
    };
    setCompetition((prev) => ({ ...prev, ...changes }));
    queueChange('dayIndices', changes.dayIndices);
    queueChange('dayIds', changes.dayIds);
    onChange?.(changes);
  };

  const handleAmplifierChange = (index: number, changes: Partial<CompAmplifier>) => {
    const updated = [...(competition.amplifiers ?? [])];
    updated[index] = { ...updated[index], ...changes };
    handleFieldChange('amplifiers', updated);
  };

  const addAmplifier = () => {
    const updated = [
      ...(competition.amplifiers ?? []),
      { scoreAmplifier: COMP_SCORE_AMPLIFIERS.MULTIPLIER, scoreAmplifierCondition: COMP_SCORE_AMPLIFIER_CONDITIONS.ALL } as CompAmplifier,
    ];
    handleFieldChange('amplifiers', updated);
  };

  const removeAmplifier = (index: number) => {
    const updated = (competition.amplifiers ?? []).filter((_, i) => i !== index);
    handleFieldChange('amplifiers', updated.length > 0 ? updated : undefined);
  };

  const handleGroupScoringChange = (field: keyof GroupScoringConfig, value: number | undefined) => {
    const updated: GroupScoringConfig = { ...competition.groupScoring, [field]: value };
    const isEmpty = !updated.grossBest && !updated.netBest && !updated.holePattern?.length;
    handleFieldChange('groupScoring', isEmpty ? undefined : updated);
  };

  // Fewer than all of the unit's players count on each hole (Competition.activePerHole). A count of
  // the whole unit clears it; a smaller count is stored together with its rotation and blind
  // choice (defaults filled in), so the saved game always says exactly how it's scored.
  const handleActivePerHoleChange = (changes: Partial<ActivePerHole>) => {
    const unitSize = resolveCompetingUnit(competition)?.size ?? 1;
    const next: ActivePerHole = {
      count: unitSize,
      rotation: DEFAULT_ACTIVE_ROTATION,
      isBlindExcluded: false,
      ...competition.activePerHole,
      ...changes,
    };
    handleFieldChange('activePerHole', next.count >= unitSize ? undefined : next);
  };

  // "Set by hole" UI stopgap for the general holePattern mechanism (rotating/per-hole best-count
  // rule, e.g. Net 1-2-3 / Cha-Cha-Cha) - always shows GROUP_SCORING_HOLE_COUNT boxes regardless of
  // the event's actual hole count, since no numHoles context reaches this editor. Rows past the
  // round's real length are simply never read by buildCombinedBucketResults. Will be reworked once
  // the Step 4 unit-formation UI (phase 4.7) lands; this only needs to exist so the path is reachable.
  const handleHoleBasedToggle = (checked: boolean) => {
    if (checked) {
      const pattern: HoleGroupScore[] = Array.from({ length: GROUP_SCORING_HOLE_COUNT }, () => ({
        ...(competition.groupScoring?.grossBest ? { gross: competition.groupScoring.grossBest } : {}),
        ...(competition.groupScoring?.netBest ? { net: competition.groupScoring.netBest } : {}),
      }));
      handleFieldChange('groupScoring', { ...competition.groupScoring, holePattern: pattern });
    } else {
      const { holePattern: _drop, ...rest } = competition.groupScoring ?? {};
      const isEmpty = !rest.grossBest && !rest.netBest;
      handleFieldChange('groupScoring', isEmpty ? undefined : rest);
    }
  };

  const handleHolePatternChange = (holeIndex: number, field: keyof HoleGroupScore, value: number | undefined) => {
    const pattern = [...(competition.groupScoring?.holePattern ?? [])];
    pattern[holeIndex] = { ...pattern[holeIndex], [field]: value };
    handleFieldChange('groupScoring', { ...competition.groupScoring, holePattern: pattern });
  };

  const handleScoreWindowChange = (index: number, changes: Partial<ScoreWindow>) => {
    const updated = [...(competition.scoreWindows ?? [])];
    updated[index] = { ...updated[index]!, ...changes };
    handleFieldChange('scoreWindows', updated);
  };

  const addScoreWindow = () => {
    const existing = competition.scoreWindows ?? [];
    const lastTo = existing.length > 0 ? existing[existing.length - 1]!.toHole : -1;
    const from = Math.min(lastTo + 1, 17);
    handleFieldChange('scoreWindows', [...existing, { fromHole: from, toHole: 17, label: '' }]);
  };

  const removeScoreWindow = (index: number) => {
    const updated = (competition.scoreWindows ?? []).filter((_, i) => i !== index);
    handleFieldChange('scoreWindows', updated.length > 0 ? updated : undefined);
  };

  const handleTiebreakerChange = (level: 0 | 1, val: CompTiebreaker | null) => {
    if (val === null) {
      const next = (competition.tiebreaker ?? []).slice(0, level);
      handleFieldChange('tiebreaker', next.length > 0 ? next : undefined);
    } else {
      const next = [...(competition.tiebreaker ?? [])].slice(0, 2) as CompTiebreaker[];
      next[level] = val;
      handleFieldChange('tiebreaker', next.slice(0, level + 1));
    }
  };

  const handleScorecardConfigChange = (patch: Partial<ScorecardConfig>) => {
    const updated = { ...competition.scorecardConfig, ...patch };
    const isEmpty = !updated.hideFromScorecard && !updated.rowLabel && !updated.extraRows?.length;
    handleFieldChange('scorecardConfig', isEmpty ? undefined : updated);
  };

  const handleExtraRowChange = (i: number, patch: Partial<ScorecardExtraRow>) => {
    const next = [...(competition.scorecardConfig?.extraRows ?? [])];
    next[i] = { ...next[i], ...patch };
    handleScorecardConfigChange({ extraRows: next });
  };
  const addExtraRow = () => {
    const next = [...(competition.scorecardConfig?.extraRows ?? []), { type: SCORECARD_EXTRA_ROW_TYPES.NOTES }];
    handleScorecardConfigChange({ extraRows: next });
  };
  const removeExtraRow = (i: number) => {
    handleScorecardConfigChange({ extraRows: (competition.scorecardConfig?.extraRows ?? []).filter((_, idx) => idx !== i) });
  };

  const onSubmit = () => {
    if (handleCreate) handleCreate(competition);
  };

  const onActionsChangeRef = useRef(onActionsChange);
  useEffect(() => { onActionsChangeRef.current = onActionsChange; });

  const handleDeleteRef = useRef(handleDelete);
  useEffect(() => { handleDeleteRef.current = handleDelete; });

  const competitionRef = useRef(competition);
  useEffect(() => { competitionRef.current = competition; });

  const competitionName = competition.name;
  const hasDelete = !!handleDelete;
  useEffect(() => {
    if (!onActionsChangeRef.current) return;

    const pageActions: ActionProps[] | null = isNew ? [
      { name: 'Create', icon: SaveIcon, type: 'submit', form: 'competition-editor-form', disabled: !competitionName?.trim(), variant: 'contained', color: 'primary' },
    ] : null;

    const godActions = !isNew && hasDelete ? (
      deleteConfirm ? (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" color="error" startIcon={<DeleteIcon />}
            onClick={() => handleDeleteRef.current?.(competitionRef.current)}>
            Confirm delete
          </Button>
          <Button size="small" onClick={() => setDeleteConfirm(false)}>Cancel</Button>
        </Stack>
      ) : (
        <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
          onClick={() => setDeleteConfirm(true)}>
          Delete
        </Button>
      )
    ) : null;

    onActionsChangeRef.current(pageActions, godActions);
  }, [competitionName, isNew, hasDelete, deleteConfirm]);

  // Derived booleans used across sections
  const isMatchScoring = MATCH_SCORINGS.has(competition.scoring ?? '' as CompScoring);
  const isSpecialScoring = SPECIAL_EVENT_SCORINGS.has(competition.scoring ?? '' as CompScoring);
  const isAdminEntered = competition.scoringMethod === COMP_SCORING_METHODS.ADMIN_ENTERED;
  const isCountScoring = competition.scoring === COMP_SCORINGS.COUNT;
  // Amplifiers (Flip the Bird, etc.) are only ever read by the Vegas compute engine — offering
  // them for every other scoring type let admins configure something with no effect anywhere.
  const isVegasScoring = !!competition.groupScoring?.concatScores;

  // Derived state for the simplified Format/Scores/Vs pickers — see the module comment above
  // entryToFormatMode for why "outside the curated subset" always reads as 'custom'.
  const formatMode: SimpleFormat = formatCustom ? 'custom' : entryToFormatMode(competition.entry);
  const scoresMode: SimpleScores = scoresCustom ? 'custom' : formatToScoresMode(competition.format);
  const vsMode: SimpleVs = vsCustom ? 'custom' : fieldToVsMode(competition.field);
  const vsUnit = vsUnitNoun(competition.entry);
  // Scores (how 2+ shared-result players combine) only applies once Format is Partner/Team —
  // Individual has nothing to combine, and an unset Format hasn't told us yet.
  const showScoresMode = formatMode === 'partner' || formatMode === 'foursome' || formatMode === 'team'
    || (formatMode === 'custom' && !!competition.entry && competition.entry !== COMP_ENTRIES.SINGLE);

  // A game that starts competing as units of more than one player (a Best Ball's pairs, a
  // Scramble's shared ball) gets its unit source stored right here, so what the editor shows is
  // what the engine will use — the same default applyUnitSourceDefault stamps on save
  // (partnerships when the event defines them at this unit's size, else tee sheet). An existing
  // source is never changed by a format edit.
  const stampUnitSourceDefault = (changes: Partial<Competition>) => {
    const next = { ...competition, ...changes };
    if (next.unitSource) return;
    const size = resolveCompetingUnit(next)?.size ?? 1;
    if (size === 1) return;
    handleFieldChange('unitSource', defaultUnitSource(size, event ?? {}));
  };
  const handleFormatModeChange = (mode: SimpleFormat) => {
    if (mode === 'custom') { setFormatCustom(true); return; }
    setFormatCustom(false);
    const entry = formatModeToEntry(mode);
    handleFieldChange('entry', entry);
    stampUnitSourceDefault({ entry });
  };
  const handleScoresModeChange = (mode: SimpleScores) => {
    if (mode === 'custom') { setScoresCustom(true); return; }
    setScoresCustom(false);
    handleFieldChange('format', mode);
    stampUnitSourceDefault({ format: mode });
  };

  // ── Who partners whom: one picker, "Seats paired" ─────────────────────────
  // One source per game (see shared/types/ScoringType.ts): the event's named partnerships, or
  // tee-sheet seats split by a pattern. Both are choices of the same thing, so they share one
  // control: "Named partners" first (offered when the event defines partnerships of this unit's
  // size, and the default then — see stampUnitSourceDefault), then every way to split the group's
  // seats. Absent unitSource means tee sheet, contiguous seats — exactly what the engine reads, so
  // the picker never shows a value the results won't use.
  const competingUnit = resolveCompetingUnit(competition);
  // Active players per hole: only for a unit playing its own balls (a one-score format has one
  // shared ball, so nobody sits out), and never fewer than the most best scores counted on a hole.
  const activeUnitSize = competingUnit?.size ?? 1;
  const offersActivePerHole = activeUnitSize > 1 && !(competition.format != null && ONE_SCORE_FORMATS.has(competition.format));
  const activeCount = competition.activePerHole?.count ?? activeUnitSize;
  const fewestActive = Math.max(
    1,
    competition.groupScoring?.grossBest ?? 0,
    competition.groupScoring?.netBest ?? 0,
    ...(competition.groupScoring?.holePattern ?? []).flatMap((h) => [h.gross ?? 0, h.net ?? 0]),
  );
  const activeRotationPreview = Array.from({ length: 4 }, (_, i) =>
    `Hole ${i + 1}: ${resolveActiveSeats(competition.activePerHole, activeUnitSize, i + 1).map((seat) => seat + 1).join('-')}`
  ).join(' · ');
  const effectiveUnitSource: ScoringUnitSource = competition.unitSource ?? SCORING_UNIT_SOURCES.TEE_POSITION;
  const teeSheetGroupSize = event?.teeSheetGroupSize ?? 4;
  const offersNamedPartners = !!competingUnit && (
    (!!event && isPartnershipEvent(event) && eventPartnershipSize(event) === competingUnit.size)
    || competition.unitSource === SCORING_UNIT_SOURCES.PARTNERSHIP
  );
  const seatOptions = competingUnit && !competingUnit.wholeGroup ? seatPatternOptions(teeSheetGroupSize, competingUnit.size) : [];
  const currentSeatPattern = competition.seatPattern?.length
    ? competition.seatPattern
    : (competingUnit ? contiguousSeatPattern(teeSheetGroupSize, competingUnit.size) : []);
  const selectedSeatIndex = Math.max(0, seatOptions.findIndex((p) => seatPatternsEqual(p, currentSeatPattern)));
  // Picker values: 'PARTNERSHIP' for named partners, else the seat-pattern option index. A
  // whole-group unit (a foursome game) has no seat split to pick, so its only tee-sheet choice is
  // the group itself (index 0).
  const NAMED_PARTNERS = 'PARTNERSHIP';
  const seatsPairedValue: string | number = effectiveUnitSource === SCORING_UNIT_SOURCES.PARTNERSHIP ? NAMED_PARTNERS : selectedSeatIndex;
  const seatsPairedChoices: { value: string | number; label: string }[] = [
    ...(offersNamedPartners ? [{ value: NAMED_PARTNERS, label: 'Named partners' }] : []),
    ...(seatOptions.length > 0
      ? seatOptions.map((p, i) => ({ value: i, label: formatSeatPattern(p) }))
      : competingUnit?.wholeGroup ? [{ value: 0, label: 'Tee sheet group' }] : []),
  ];
  const showSeatsPaired = !!competingUnit && seatsPairedChoices.length > 1;
  const handleSeatsPairedChange = (value: string | number) => {
    if (value === NAMED_PARTNERS) {
      handleFieldChange('unitSource', SCORING_UNIT_SOURCES.PARTNERSHIP);
      handleFieldChange('seatPattern', undefined);
      return;
    }
    const index = Number(value);
    if (competition.unitSource !== SCORING_UNIT_SOURCES.TEE_POSITION) handleFieldChange('unitSource', SCORING_UNIT_SOURCES.TEE_POSITION);
    // Index 0 is always the contiguous split, the pattern-less default — stored as no pattern at
    // all so the game keeps today's keys and rows exactly.
    handleFieldChange('seatPattern', index === 0 ? undefined : seatOptions[index]);
  };

  const handleVsModeChange = (mode: SimpleVs) => {
    if (mode === 'custom') { setVsCustom(true); return; }
    setVsCustom(false);
    handleFieldChange('field', vsModeToField(mode));
  };

  const gameMode: SimpleGame = gameCustom ? 'custom' : scoringToGameMode(competition.scoring);
  const handleGameModeChange = (mode: SimpleGame) => {
    if (mode === 'custom') { setGameCustom(true); return; }
    setGameCustom(false);
    // handleScoringChange (not a plain handleFieldChange) so this still goes through
    // inferCompetitionParams — same scoringMethod/aggregation inference as picking from the raw
    // Scoring dropdown always got.
    handleScoringChange(gameModeToScoring(mode));
  };

  return (
    <Container disableGutters className="SectionContainer">
      <Box id="competition-editor-form" component="form" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
        <Stack spacing={0}>

          {/* Name — always visible */}
          <Box sx={{ py: 1.5 }}>
            <TextField
              id="name"
              label="Name"
              required
              fullWidth
              value={competition.name ?? ''}
              onChange={({ target }) => handleFieldChange('name', target.value)}
            />
          </Box>

          {/* ── WHO PLAYS ── */}
          <CollapsibleSection
            sectionRef={whoRef}
            title="Players"
            isOpen={openSections.has('who')}
            onToggle={() => toggleSection('who')}
          >
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Format</Typography>
              <ToggleButtonGroup
                value={formatMode}
                exclusive
                size="small"
                onChange={(_, val) => { if (val) handleFormatModeChange(val as SimpleFormat); }}
              >
                <ToggleButton value="individual">{FORMAT_MODE_LABELS.individual}</ToggleButton>
                <ToggleButton value="partner">{FORMAT_MODE_LABELS.partner}</ToggleButton>
                <ToggleButton value="foursome">{FORMAT_MODE_LABELS.foursome}</ToggleButton>
                <ToggleButton value="team">{FORMAT_MODE_LABELS.team}</ToggleButton>
                <ToggleButton value="custom">Custom</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {formatMode === 'custom' && (
              <TextField
                id="entry"
                label="Entry"
                select
                value={competition.entry ?? ''}
                onChange={({ target }) => handleFieldChange('entry', (target.value || undefined) as CompEntry | undefined)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {ALL_COMP_ENTRIES.map((v) => (
                  <MenuItem key={v} value={v}>{formatEnumLabel(v)}</MenuItem>
                ))}
              </TextField>
            )}

            {showScoresMode && (
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Scores</Typography>
                <ToggleButtonGroup
                  value={scoresMode}
                  exclusive
                  size="small"
                  onChange={(_, val) => { if (val) handleScoresModeChange(val as SimpleScores); }}
                  sx={{ flexWrap: 'wrap' }}
                >
                  {SIMPLE_SCORES_FORMATS.map((f) => (
                    <ToggleButton key={f} value={f}>{SCORES_MODE_LABELS[f]}</ToggleButton>
                  ))}
                  <ToggleButton value="custom">Custom</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            )}

            {showScoresMode && scoresMode === 'custom' && (
              <TextField
                id="format"
                label="Format"
                select
                value={competition.format ?? ''}
                onChange={({ target }) => handleFieldChange('format', (target.value || undefined) as CompFormat | undefined)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {ALL_COMP_FORMATS.map((v) => (
                  <MenuItem key={v} value={v}>{formatEnumLabel(v)}</MenuItem>
                ))}
              </TextField>
            )}

            {showSeatsPaired && (
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Seats paired</Typography>
                {seatsPairedChoices.length <= 4 ? (
                  <ToggleButtonGroup
                    value={seatsPairedValue}
                    exclusive
                    size="small"
                    onChange={(_, val) => { if (val !== null) handleSeatsPairedChange(val as string | number); }}
                  >
                    {seatsPairedChoices.map((c) => (
                      <ToggleButton key={String(c.value)} value={c.value}>{c.label}</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                ) : (
                  <TextField
                    id="seatsPaired"
                    select
                    size="small"
                    value={seatsPairedValue}
                    onChange={({ target }) => handleSeatsPairedChange(target.value === NAMED_PARTNERS ? NAMED_PARTNERS : Number(target.value))}
                  >
                    {seatsPairedChoices.map((c) => (
                      <MenuItem key={String(c.value)} value={c.value}>{c.label}</MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            )}

            {/* Vs (comparison pool) and match play are independent axes, not "if match play,
                hide Vs" — Match Play is a scoring system that's only ever applicable head-to-head
                (2 units facing off), which for THIS engine means: WHICH physical unit you face
                comes from tee-sheet position, not from `field` (see matchEntries.ts) — `field`
                genuinely doesn't drive that. But nothing stops an admin from also setting Field/
                Flight/Division/Custom here (confirmed inert for match play, not disallowed —
                we don't block nonsensical-but-harmless combos), so the picker stays live; the
                caption below just states what's actually true for match play. */}
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Vs</Typography>
              <ToggleButtonGroup
                value={vsMode}
                exclusive
                size="small"
                onChange={(_, val) => { if (val) handleVsModeChange(val as SimpleVs); }}
              >
                {/* "All Pairs"/"Pairs in Flight" once entry is a pair — otherwise "vs. Field"
                    reads as if it means individuals, when a pair entry already ranks pairs
                    against pairs. Labeling fix only; entry=PAIR + field=ALL already works. */}
                <ToggleButton value="field">{vsUnit ? `All ${vsUnit}` : VS_MODE_LABELS.field}</ToggleButton>
                <ToggleButton value="flight">{vsUnit ? `${vsUnit} in Flight` : VS_MODE_LABELS.flight}</ToggleButton>
                <ToggleButton value="division">{vsUnit ? `${vsUnit} in Division` : VS_MODE_LABELS.division}</ToggleButton>
                <ToggleButton value="physicalGroup">{vsUnit ? `${vsUnit} in Group` : VS_MODE_LABELS.physicalGroup}</ToggleButton>
                <ToggleButton value="custom">Custom</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {vsMode === 'physicalGroup' && gameMode === 'skins' && (
              // Confirmed inert, not wrong: classifyAndRerankByPhysicalGroup (resultDispatch.ts)
              // explicitly skips Skins — its per-hole winner needs to be decided within the group,
              // not after, the same reason INDEX needed its own in-line treatment for Skins. Not
              // blocked in the picker (a harmless no-op, same "don't disallow" rule as everywhere
              // else) — just stated plainly instead of silently doing nothing.
              <Typography variant="caption" color="text.secondary">
                Skins doesn&apos;t support Pairing Group scoping yet — this game will be scored
                against the whole field instead.
              </Typography>
            )}

            {isMatchScoring && (
              // "Pair", not "Partner" — Format's "Partner" describes your own teammate; who
              // you face is another pair, not your partner.
              <Typography variant="caption" color="text.secondary">
                Match play&apos;s opponent is decided by tee sheet pairing
                ({formatMode === 'individual' ? 'Individual'
                  : formatMode === 'partner' ? 'Pair'
                  : formatMode === 'foursome' ? 'Foursome'
                  : 'same as Format'}), not by the Vs setting above.
              </Typography>
            )}

            {vsMode === 'custom' && (
              <TextField
                id="field"
                label="Field"
                select
                value={competition.field ?? ''}
                onChange={({ target }) => handleFieldChange('field', (target.value || undefined) as CompField | undefined)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {ALL_COMP_FIELDS.map((v) => (
                  <MenuItem key={v} value={v}>{formatEnumLabel(v)}</MenuItem>
                ))}
              </TextField>
            )}

            {competition.field === COMP_FIELDS.INDEX && (
              <Stack spacing={1}>
                <Typography variant="subtitle2">Eligibility windows</Typography>
                <Typography variant="body2" color="text.secondary">
                  Splits the field into groups by handicap value and ranks each independently — e.g.
                  Skins for 15-and-under {HANDICAP_LABELS.PLAYING_HANDICAP} and Skins for over-15, as two separate pots.
                </Typography>
                <IndexBracketListEditor
                  brackets={competition.indexBrackets ?? []}
                  entry={competition.entry}
                  onChange={(updated) => handleFieldChange('indexBrackets', updated.length > 0 ? updated : undefined)}
                />
              </Stack>
            )}

            {(() => {
              const groupingType =
                competition.field === 'FLIGHT' ? GROUPING_TYPES.FLIGHT :
                competition.field === 'DIVISION' ? GROUPING_TYPES.DIVISION : null;
              const scoped = groupingType ? (groupings ?? []).filter(g => g.type === groupingType) : [];
              if (!scoped.length) return null;
              const selected = competition.applicableGroupingIds ?? [];
              return (
                <TextField
                  label={`Applicable ${formatEnumLabel(groupingType!)}s`}
                  select
                  slotProps={{ select: { multiple: true, renderValue: (v) =>
                    (v as string[]).map(id => scoped.find(g => g.itemId === id)?.name ?? id).join(', ')
                  }}}
                  value={selected}
                  onChange={({ target }) =>
                    handleFieldChange('applicableGroupingIds', (target.value as unknown as string[]).length ? target.value as unknown as string[] : undefined)
                  }
                >
                  {scoped.map(g => (
                    <MenuItem key={g.itemId} value={g.itemId}>
                      <Checkbox checked={selected.includes(g.itemId)} />
                      <ListItemText primary={g.name} />
                    </MenuItem>
                  ))}
                </TextField>
              );
            })()}

            <Typography variant="overline" sx={{ color: 'text.disabled', pt: 1 }}>Advanced</Typography>

            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Is team?</Typography>
              <ToggleButtonGroup
                value={competition.isTeam ? 'yes' : 'no'}
                exclusive
                size="small"
                onChange={(_, val) => { if (val !== null) handleFieldChange('isTeam', val === 'yes'); }}
              >
                <ToggleButton value="yes">Yes</ToggleButton>
                <ToggleButton value="no">No</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {competition.isTeam && (
              <TextField
                id="teamSize"
                label="Team size"
                type="number"
                slotProps={{ htmlInput: { min: 2, max: 8 } }}
                value={competition.teamSize ?? ''}
                onChange={({ target }) =>
                  handleFieldChange('teamSize', target.value ? parseInt(target.value, 10) : undefined)
                }
              />
            )}

            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Roll up to team?</Typography>
              <ToggleButtonGroup
                value={competition.teamRollup ? 'yes' : 'no'}
                exclusive
                size="small"
                onChange={(_, val) => { if (val !== null) handleFieldChange('teamRollup', val === 'yes'); }}
              >
                <ToggleButton value="yes">Yes</ToggleButton>
                <ToggleButton value="no">No</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {competition.teamRollup && (
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Hide pair detail?</Typography>
                <ToggleButtonGroup
                  value={competition.hidePairDetail ? 'yes' : 'no'}
                  exclusive
                  size="small"
                  onChange={(_, val) => { if (val !== null) handleFieldChange('hidePairDetail', val === 'yes'); }}
                >
                  <ToggleButton value="yes">Yes</ToggleButton>
                  <ToggleButton value="no">No</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            )}

            {/* Structure (Bracket/Round Robin/etc.) and per-player exclusion/buy-in status
                intentionally don't appear here — see git history for why. Structure has no reader
                anywhere in the results engine; excluding a player from a game (or marking them
                paid) is a tee-sheet-level action, done (and shown) in
                EventGameParticipationMatrix, not duplicated as a read-only mirror in this settings
                form. Only the buy-in AMOUNT (the game's own cost, not any one player's status)
                belongs here, alongside the game's other rules. */}

            <TextField
              id="buyInAmount"
              label="Buy-in amount"
              type="number"
              size="small"
              sx={{ width: '10em' }}
              slotProps={{ htmlInput: { min: 0, step: 1 } }}
              value={competition.buyInAmount ?? ''}
              helperText="A player must pay this to play — set once here, tracked per-player on the tee sheet"
              onChange={({ target }) => handleFieldChange('buyInAmount', target.value ? parseFloat(target.value) : undefined)}
            />

            {/* Per-game player opt in/out — overridden for every game when the event itself doesn't
                offer players game choice (see isPlayerGameChoiceOffered) */}
            {event && (() => {
              const isOffered = isPlayerGameChoiceOffered({ ...event, attendeeOptions });
              return (
                <FormControlLabel
                  disabled={!isOffered}
                  control={
                    <Checkbox
                      checked={isOffered && (competition.isOptional ?? true)}
                      onChange={(e) => handleFieldChange('isOptional', e.target.checked)}
                    />
                  }
                  label={isOffered
                    ? 'Players can opt in or out of this game'
                    : isPartnershipEvent(event)
                      ? 'Players can opt in or out of this game (not with named partners)'
                      : 'Players can opt in or out of this game (game choice is off for this event)'}
                />
              );
            })()}

            {(attendeeOptions?.includes(ATTENDEE_OPTIONS.FOLLOW_FIELD) ?? false) && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={competition.includeFollowers ?? false}
                    onChange={(e) => handleFieldChange('includeFollowers', e.target.checked || undefined)}
                  />
                }
                label="Also score field followers (separate leaderboard, never mixed with the main field)"
              />
            )}
          </CollapsibleSection>

          {/* ── SCORING ── */}
          <CollapsibleSection
            sectionRef={scoringRef}
            title="Scoring"
            isOpen={openSections.has('scoring')}
            onToggle={() => toggleSection('scoring')}
          >
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Game</Typography>
              <ToggleButtonGroup
                value={gameMode}
                exclusive
                size="small"
                onChange={(_, val) => { if (val) handleGameModeChange(val as SimpleGame); }}
                sx={{ flexWrap: 'wrap' }}
              >
                <ToggleButton value="match">{GAME_MODE_LABELS.match}</ToggleButton>
                <ToggleButton value="stroke">{GAME_MODE_LABELS.stroke}</ToggleButton>
                <ToggleButton value="stableford">{GAME_MODE_LABELS.stableford}</ToggleButton>
                <ToggleButton value="skins">{GAME_MODE_LABELS.skins}</ToggleButton>
                <ToggleButton value="nassau">{GAME_MODE_LABELS.nassau}</ToggleButton>
                <ToggleButton value="custom">Custom</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {gameMode === 'custom' && (
              <TextField
                id="scoring"
                label="Scoring"
                select
                value={competition.scoring ?? ''}
                onChange={({ target }) => handleScoringChange((target.value || undefined) as CompScoring | undefined)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {ALL_COMP_SCORINGS.map((v) => (
                  <MenuItem key={v} value={v}>{COMPETITION_PRESETS[v]?.name ?? formatEnumLabel(v)}</MenuItem>
                ))}
              </TextField>
            )}

            <Stack spacing={0.5}>
              <EventDayHolesToggle
                numHoles={competition.numHoles}
                which9={competition.which9}
                onChange={(numHoles, which9) => {
                  handleFieldChange('numHoles', numHoles);
                  handleFieldChange('which9', which9);
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Untouched, this game follows the day&apos;s own holes-played setting. Change it here
                only when this specific game should be scored on fewer holes than the rest of the
                day (e.g. a weather-shortened round where side games still count every hole actually
                played).
              </Typography>
            </Stack>

            {isSpecialScoring ? (
              <TextField
                id="holes"
                label="Holes"
                select
                slotProps={{ select: { multiple: true, renderValue: (selected) =>
                  (selected as number[]).map((position) => String(holeLabelByPosition.get(position) ?? position)).join(', ')
                }}}
                value={competition.holes ?? []}
                onChange={({ target }) =>
                  handleFieldChange('holes', (target.value as unknown as number[]).sort((a, b) => a - b))
                }
              >
                {holeChoices.map(({ position, holeNumber }) => (
                  <MenuItem key={position} value={position}>
                    <Checkbox checked={(competition.holes ?? []).includes(position)} />
                    <ListItemText primary={`Hole ${holeNumber}`} />
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <>
                {!isCountScoring && (
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">Aggregation</Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={competition.aggregation ?? COMP_AGGREGATIONS.TOTAL}
                      onChange={(_, val) => { if (val) handleFieldChange('aggregation', val as CompAggregation); }}
                    >
                      <ToggleButton value={COMP_AGGREGATIONS.TOTAL}>Total</ToggleButton>
                      <ToggleButton value={COMP_AGGREGATIONS.WINDOWS}>Windows</ToggleButton>
                      <ToggleButton value={COMP_AGGREGATIONS.SKINS}>Skins</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                )}

                {competition.scoringMethod === COMP_SCORING_METHODS.MATCH_WINS
                  && competition.aggregation === COMP_AGGREGATIONS.WINDOWS
                  && (competition.scoreWindows?.length ?? 0) === 0 && (
                  <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Early termination</Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={competition.earlyTermination ?? true}
                      onChange={(_, val) => {
                        if (val !== null) handleFieldChange('earlyTermination', val === true ? undefined : false);
                      }}
                    >
                      <ToggleButton value={true}>Yes</ToggleButton>
                      <ToggleButton value={false}>No</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                )}

                {competition.aggregation === COMP_AGGREGATIONS.WINDOWS && (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Score Windows</Typography>
                    {(competition.scoreWindows ?? []).map((win, i) => (
                      <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'flex-start' }}>
                        <TextField
                          label="Label"
                          size="small"
                          value={win.label}
                          onChange={({ target }) => handleScoreWindowChange(i, { label: target.value })}
                          sx={{ flexGrow: 1 }}
                          slotProps={{ htmlInput: { maxLength: 30 } }}
                        />
                        <TextField
                          label="From hole"
                          type="number"
                          size="small"
                          sx={{ width: '6rem' }}
                          slotProps={{ htmlInput: { min: 1, max: 18 } }}
                          value={win.fromHole + 1}
                          onChange={({ target }) =>
                            handleScoreWindowChange(i, { fromHole: Math.max(0, parseInt(target.value, 10) - 1) })
                          }
                        />
                        <TextField
                          label="To hole"
                          type="number"
                          size="small"
                          sx={{ width: '6rem' }}
                          slotProps={{ htmlInput: { min: 1, max: 18 } }}
                          value={win.toHole + 1}
                          onChange={({ target }) =>
                            handleScoreWindowChange(i, { toHole: Math.max(0, parseInt(target.value, 10) - 1) })
                          }
                        />
                        {competition.scoringMethod === COMP_SCORING_METHODS.MATCH_WINS && (
                          <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                            <Typography variant="caption" color="text.secondary">Early end</Typography>
                            <ToggleButtonGroup
                              exclusive
                              size="small"
                              value={win.earlyTermination ? 'yes' : 'no'}
                              onChange={(_, val) => {
                                if (val !== null) handleScoreWindowChange(i, { earlyTermination: val === 'yes' || undefined });
                              }}
                            >
                              <ToggleButton value="yes">Yes</ToggleButton>
                              <ToggleButton value="no">No</ToggleButton>
                            </ToggleButtonGroup>
                          </Stack>
                        )}
                        <IconButton
                          size="small"
                          aria-label="Remove window"
                          onClick={() => removeScoreWindow(i)}
                          sx={{ alignSelf: 'center' }}
                        >
                          <RemoveCircleOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addScoreWindow}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Add window
                    </Button>
                  </Stack>
                )}

                {competition.aggregation === COMP_AGGREGATIONS.SKINS && (
                  <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Carryover?</Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={competition.carryover !== false ? 'yes' : 'no'}
                      onChange={(_, val) => { if (val !== null) handleFieldChange('carryover', val === 'yes'); }}
                    >
                      <ToggleButton value="yes">Yes</ToggleButton>
                      <ToggleButton value="no">No</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                )}

                {competition.scoringMethod === COMP_SCORING_METHODS.POINTS && (
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">Points table</Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={competition.pointsTable ?? COMP_POINTS_TABLES.STANDARD}
                      onChange={(_, val) => { if (val) handleFieldChange('pointsTable', val); }}
                    >
                      <ToggleButton value={COMP_POINTS_TABLES.STANDARD}>Standard</ToggleButton>
                      <ToggleButton value={COMP_POINTS_TABLES.MODIFIED}>Modified</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                )}

                {competition.entry && competition.entry !== COMP_ENTRIES.SINGLE && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      id="groupScoring-grossBest"
                      label="Best gross scores"
                      type="number"
                      fullWidth
                      slotProps={{ htmlInput: { min: 0 } }}
                      value={competition.groupScoring?.grossBest ?? ''}
                      onChange={({ target }) => handleGroupScoringChange('grossBest', target.value ? Number(target.value) : undefined)}
                    />
                    <TextField
                      id="groupScoring-netBest"
                      label="Best net scores"
                      type="number"
                      fullWidth
                      slotProps={{ htmlInput: { min: 0 } }}
                      value={competition.groupScoring?.netBest ?? ''}
                      onChange={({ target }) => handleGroupScoringChange('netBest', target.value ? Number(target.value) : undefined)}
                    />
                  </Stack>
                )}

                {competition.entry && competition.entry !== COMP_ENTRIES.SINGLE && offersActivePerHole && (
                  <Stack spacing={2}>
                    <TextField
                      id="activePerHole-count"
                      label="Players counting on each hole"
                      type="number"
                      slotProps={{ htmlInput: { min: fewestActive, max: activeUnitSize } }}
                      value={activeCount}
                      error={activeCount < fewestActive}
                      helperText={activeCount < fewestActive
                        ? `At least ${fewestActive}, the number of best scores counted`
                        : `Of ${activeUnitSize} on the team`}
                      onChange={({ target }) => handleActivePerHoleChange({ count: target.value ? Number(target.value) : activeUnitSize })}
                    />
                    {competition.activePerHole && (
                      <>
                        <TextField
                          id="activePerHole-rotation"
                          label="Who counts on each hole"
                          select
                          value={competition.activePerHole.rotation ?? DEFAULT_ACTIVE_ROTATION}
                          helperText={activeRotationPreview}
                          onChange={({ target }) => handleActivePerHoleChange({ rotation: target.value as ActiveRotation })}
                        >
                          {ALL_ACTIVE_ROTATIONS.map((r) => (
                            <MenuItem key={r} value={r}>{ACTIVE_ROTATION_LABELS[r]}</MenuItem>
                          ))}
                        </TextField>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={competition.activePerHole.isBlindExcluded}
                              onChange={(_, checked) => handleActivePerHoleChange({ isBlindExcluded: checked })}
                            />
                          }
                          label="Leave blinds out — only real players count"
                        />
                      </>
                    )}
                  </Stack>
                )}

                {competition.entry && competition.entry !== COMP_ENTRIES.SINGLE && (
                  <Stack spacing={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!competition.groupScoring?.holePattern?.length}
                          onChange={(_, checked) => handleHoleBasedToggle(checked)}
                        />
                      }
                      label="Set by hole"
                    />
                    {!!competition.groupScoring?.holePattern?.length && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(5rem, 1fr))', gap: 1 }}>
                        {Array.from({ length: GROUP_SCORING_HOLE_COUNT }, (_, i) => (
                          <Stack key={i} spacing={0.5} sx={{ alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">Hole {i + 1}</Typography>
                            <TextField
                              label="Gross"
                              type="number"
                              size="small"
                              slotProps={{ htmlInput: { min: 0 } }}
                              value={competition.groupScoring?.holePattern?.[i]?.gross ?? ''}
                              onChange={({ target }) => handleHolePatternChange(i, 'gross', target.value ? Number(target.value) : undefined)}
                            />
                            <TextField
                              label="Net"
                              type="number"
                              size="small"
                              slotProps={{ htmlInput: { min: 0 } }}
                              value={competition.groupScoring?.holePattern?.[i]?.net ?? ''}
                              onChange={({ target }) => handleHolePatternChange(i, 'net', target.value ? Number(target.value) : undefined)}
                            />
                          </Stack>
                        ))}
                      </Box>
                    )}
                  </Stack>
                )}

                {isMatchScoring && (
                  <Stack spacing={2}>
                    <Typography variant="overline" sx={{ color: 'text.disabled' }}>Match scoring</Typography>

                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">Total is points per</Typography>
                      <ToggleButtonGroup
                        value={competition.matchTotalBasis === MATCH_TOTAL_BASES.GAME ? MATCH_TOTAL_BASES.GAME : MATCH_TOTAL_BASES.HOLES}
                        exclusive
                        size="small"
                        onChange={(_, val) => {
                          if (val !== null) handleFieldChange('matchTotalBasis', val === MATCH_TOTAL_BASES.GAME ? MATCH_TOTAL_BASES.GAME : undefined);
                        }}
                      >
                        <ToggleButton value={MATCH_TOTAL_BASES.HOLES}>Holes won</ToggleButton>
                        <ToggleButton value={MATCH_TOTAL_BASES.GAME}>Win</ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>

                    <MatchOutcomeScoreFields
                      label="Hole scores"
                      value={competition.matchHoleScores}
                      defaults={DEFAULT_MATCH_HOLE_SCORES}
                      onChange={(value) => handleFieldChange('matchHoleScores', value)}
                    />

                    {competition.matchTotalBasis === MATCH_TOTAL_BASES.GAME && (
                      <MatchOutcomeScoreFields
                        label="Game scores"
                        value={competition.matchGameScores}
                        defaults={DEFAULT_MATCH_GAME_SCORES}
                        onChange={(value) => handleFieldChange('matchGameScores', value)}
                      />
                    )}
                  </Stack>
                )}

                <TextField
                  id="scoreFilters"
                  label="Scores that count"
                  select
                  slotProps={{ select: { multiple: true, renderValue: (selected) => {
                    const keys = selected as string[];
                    return keys.map(k => SCORE_FILTER_PRESETS.find(p => scoreFilterKey(p.filter) === k)?.label ?? k).join(', ');
                  }}}}
                  value={(competition.scoreFilters ?? []).map(scoreFilterKey)}
                  onChange={({ target }) => {
                    const keys = target.value as unknown as string[];
                    const filters = keys
                      .map(k => SCORE_FILTER_PRESETS.find(p => scoreFilterKey(p.filter) === k)?.filter)
                      .filter((f): f is ScoreFilter => f !== undefined);
                    handleFieldChange('scoreFilters', filters.length > 0 ? filters : undefined);
                  }}
                >
                  {SCORE_FILTER_PRESETS.map(({ label, filter }) => {
                    const key = scoreFilterKey(filter);
                    return (
                      <MenuItem key={key} value={key}>
                        <Checkbox checked={(competition.scoreFilters ?? []).some(f => scoreFilterKey(f) === key)} />
                        <ListItemText primary={label} />
                      </MenuItem>
                    );
                  })}
                </TextField>

                {isVegasScoring && (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Amplifiers</Typography>
                    {(competition.amplifiers ?? []).map((amp, i) => (
                      <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'flex-start' }}>
                        <TextField
                          label="Amplifier"
                          select
                          size="small"
                          sx={{ minWidth: '10rem' }}
                          value={amp.scoreAmplifier}
                          onChange={({ target }) =>
                            handleAmplifierChange(i, { scoreAmplifier: target.value as CompScoreAmplifier })
                          }
                        >
                          {ALL_COMP_SCORE_AMPLIFIERS.map((v) => (
                            <MenuItem key={v} value={v}>{formatEnumLabel(v)}</MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          label="Condition"
                          select
                          size="small"
                          sx={{ minWidth: '10rem' }}
                          value={amp.scoreAmplifierCondition}
                          onChange={({ target }) =>
                            handleAmplifierChange(i, { scoreAmplifierCondition: target.value as CompScoringAmplifierCondition })
                          }
                        >
                          {ALL_COMP_SCORE_AMPLIFIER_CONDITIONS.map((v) => (
                            <MenuItem key={v} value={v}>{formatEnumLabel(v)}</MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          label="Amount"
                          type="number"
                          size="small"
                          sx={{ width: '6rem' }}
                          value={amp.scoreAmplifierAmount ?? ''}
                          onChange={({ target }) =>
                            handleAmplifierChange(i, {
                              scoreAmplifierAmount: target.value ? parseFloat(target.value) : undefined,
                            })
                          }
                        />
                        <IconButton
                          size="small"
                          aria-label="Remove amplifier"
                          onClick={() => removeAmplifier(i)}
                          sx={{ alignSelf: 'center' }}
                        >
                          <RemoveCircleOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} onClick={addAmplifier} sx={{ alignSelf: 'flex-start' }}>
                      Add amplifier
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </CollapsibleSection>

          {/* ── HANDICAP ── */}
          <CollapsibleSection
            sectionRef={handicapRef}
            title="Handicap"
            isOpen={openSections.has('handicap')}
            onToggle={() => toggleSection('handicap')}
          >
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">Handicap</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={competition.handicap ?? ''}
                onChange={(_, val) => { if (val) handleFieldChange('handicap', val as CompHandicap); }}
              >
                <ToggleButton value={COMP_HANDICAPS.GROSS}>Gross</ToggleButton>
                <ToggleButton value={COMP_HANDICAPS.NET}>Net</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {competition.handicap === COMP_HANDICAPS.NET && (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  {(!competition.entry || competition.entry === COMP_ENTRIES.SINGLE) && (
                    <TextField
                      id="handicapPct"
                      label="Handicap allowance %"
                      type="number"
                      fullWidth
                      slotProps={{ htmlInput: { min: 0, max: 100 } }}
                      value={competition.handicapPctByUnitSize?.[1]?.[0] ?? competition.handicapPct ?? ''}
                      onChange={({ target }) => {
                        const pct = target.value ? parseFloat(target.value) : undefined;
                        // Canonical source is now the table (see HandicapPctByUnitSizeEditor's own
                        // comment) — an individual competition just has the one field, writing into
                        // its unit-size-1 row. handicapPct is kept mirrored for back-compat.
                        handleFieldChange('handicapPctByUnitSize', pct !== undefined ? { ...competition.handicapPctByUnitSize, 1: [pct] } : competition.handicapPctByUnitSize);
                        handleFieldChange('handicapPct', pct);
                      }}
                    />
                  )}
                  <TextField
                    id="handicapMax"
                    label={`Max ${HANDICAP_LABELS.INDEX}`}
                    type="number"
                    fullWidth
                    slotProps={{ htmlInput: { min: 0, max: 54 } }}
                    value={competition.handicapMax ?? ''}
                    onChange={({ target }) =>
                      handleFieldChange('handicapMax', target.value ? parseInt(target.value, 10) : undefined)
                    }
                  />
                  <TextField
                    id="handicapCourseMax"
                    label={`Max ${HANDICAP_LABELS.COURSE_HANDICAP}`}
                    type="number"
                    fullWidth
                    slotProps={{ htmlInput: { min: 0 } }}
                    value={competition.handicapCourseMax ?? ''}
                    onChange={({ target }) =>
                      handleFieldChange('handicapCourseMax', target.value ? parseInt(target.value, 10) : undefined)
                    }
                  />
                </Stack>

                {competition.entry && competition.entry !== COMP_ENTRIES.SINGLE && (
                  <HandicapPctByUnitSizeEditor
                    competition={competition}
                    onChange={(next) => handleFieldChange('handicapPctByUnitSize', next)}
                  />
                )}

                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">Handicap basis</Typography>
                  <ToggleButtonGroup
                    value={competition.netHandicapBasis ?? COMP_NET_HANDICAP_BASIS.COURSE}
                    exclusive
                    size="small"
                    onChange={(_, val) => { if (val) handleFieldChange('netHandicapBasis', val as CompNetHandicapBasis); }}
                  >
                    {ALL_COMP_NET_HANDICAP_BASES.map((v) => (
                      <ToggleButton key={v} value={v}>{formatEnumLabel(v)}</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Stack>
              </>
            )}
          </CollapsibleSection>

          {/* ── RULES ── */}
          <CollapsibleSection
            sectionRef={rulesRef}
            title="Rules"
            isOpen={openSections.has('rules')}
            onToggle={() => toggleSection('rules')}
          >
            {!isMatchScoring && !isSpecialScoring && !isAdminEntered && (() => {
              const primary = competition.tiebreaker?.[0] ?? null;
              const secondary = competition.tiebreaker?.[1] ?? null;
              const showSecondary = primary !== null &&
                primary !== COMP_TIEBREAKERS.NONE &&
                primary !== COMP_TIEBREAKERS.PLAYOFF &&
                primary !== COMP_TIEBREAKERS.CUSTOM;
              const hasCustom = competition.tiebreaker?.includes(COMP_TIEBREAKERS.CUSTOM);
              return (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Tiebreaker</Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">Primary</Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={primary}
                      sx={{ flexWrap: 'wrap', gap: 0.5 }}
                      onChange={(_, val) => handleTiebreakerChange(0, val)}
                    >
                      {ALL_COMP_TIEBREAKERS.map((tb) => (
                        <ToggleButton key={tb} value={tb} sx={{ py: 0.25, px: 1 }}>
                          {TIEBREAKER_LABELS[tb]}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Stack>
                  {showSecondary && (
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">Secondary</Typography>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={secondary}
                        sx={{ flexWrap: 'wrap', gap: 0.5 }}
                        onChange={(_, val) => handleTiebreakerChange(1, val)}
                      >
                        {ALL_COMP_TIEBREAKERS.filter((tb) => tb !== primary).map((tb) => (
                          <ToggleButton key={tb} value={tb} sx={{ py: 0.25, px: 1 }}>
                            {TIEBREAKER_LABELS[tb]}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Stack>
                  )}
                  {hasCustom && (
                    <TextField
                      label="Tiebreaker description"
                      size="small"
                      value={competition.tiebreakerCustom ?? ''}
                      onChange={({ target }) => handleFieldChange('tiebreakerCustom', target.value || undefined)}
                      slotProps={{ htmlInput: { maxLength: 200 } }}
                    />
                  )}
                </Stack>
              );
            })()}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
              <TextField
                id="holeCapType"
                label="Hole score cap"
                select
                fullWidth
                value={competition.holeCapType ?? COMP_HOLE_CAP_TYPES.NONE}
                onChange={({ target }) => handleFieldChange('holeCapType', target.value as CompHoleCapType)}
              >
                {Object.values(COMP_HOLE_CAP_TYPES).map((v) => (
                  <MenuItem key={v} value={v}>{formatEnumLabel(v)}</MenuItem>
                ))}
              </TextField>
              {competition.holeCapType && competition.holeCapType !== COMP_HOLE_CAP_TYPES.NONE && (
                <TextField
                  id="holeCapValue"
                  label={competition.holeCapType === COMP_HOLE_CAP_TYPES.ABSOLUTE ? 'Max strokes' : 'Strokes over par'}
                  type="number"
                  fullWidth
                  slotProps={{ htmlInput: { min: 0, max: 20 } }}
                  value={competition.holeCapValue ?? ''}
                  onChange={({ target }) =>
                    handleFieldChange('holeCapValue', target.value ? parseInt(target.value, 10) : undefined)
                  }
                />
              )}
            </Stack>

            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Hide from scorecards?</Typography>
              <ToggleButtonGroup
                value={competition.scorecardConfig?.hideFromScorecard ? 'yes' : 'no'}
                exclusive
                size="small"
                onChange={(_, val) => {
                  if (val !== null) handleScorecardConfigChange({ hideFromScorecard: val === 'yes' || undefined });
                }}
              >
                <ToggleButton value="yes">Yes</ToggleButton>
                <ToggleButton value="no">No</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {!competition.scorecardConfig?.hideFromScorecard && (
              <TextField
                label="Scorecard row label"
                size="small"
                placeholder="Auto"
                value={competition.scorecardConfig?.rowLabel ?? ''}
                onChange={({ target }) => handleScorecardConfigChange({ rowLabel: target.value || undefined })}
                slotProps={{ htmlInput: { maxLength: 20 } }}
              />
            )}

            {!competition.scorecardConfig?.hideFromScorecard && (
              <Stack spacing={1}>
                <Typography variant="subtitle2">Extra scorecard rows</Typography>
                {(competition.scorecardConfig?.extraRows ?? []).map((row, i) => (
                  <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <TextField
                      label="Type"
                      select
                      size="small"
                      sx={{ minWidth: '10rem' }}
                      value={row.type}
                      onChange={({ target }) => handleExtraRowChange(i, { type: target.value as ScorecardExtraRowType })}
                    >
                      {ALL_SCORECARD_EXTRA_ROW_TYPES.map((v) => (
                        <MenuItem key={v} value={v}>{formatEnumLabel(v)}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Label"
                      size="small"
                      placeholder="Auto"
                      value={row.label ?? ''}
                      onChange={({ target }) => handleExtraRowChange(i, { label: target.value || undefined })}
                      slotProps={{ htmlInput: { maxLength: 20 } }}
                    />
                    <IconButton
                      size="small"
                      aria-label="Remove extra row"
                      onClick={() => removeExtraRow(i)}
                      sx={{ alignSelf: 'center' }}
                    >
                      <RemoveCircleOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={addExtraRow} sx={{ alignSelf: 'flex-start' }}>
                  Add extra row
                </Button>
              </Stack>
            )}
          </CollapsibleSection>

          {/* ── LEAGUE ── */}
          <CollapsibleSection
            sectionRef={leagueRef}
            title="League"
            isOpen={openSections.has('league')}
            onToggle={() => toggleSection('league')}
          >
            {(() => {
              const leagueComps: LeagueCompetition[] = league?.leagueCompetitions ?? [];
              if (leagueComps.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No league competitions configured.
                  </Typography>
                );
              }

              const isMatchScoringVal = (s: string | null | undefined) =>
                s === COMP_SCORINGS.MATCH || s === COMP_SCORINGS.MATCH_FULL_ROUND;
              const entryMatches = (lc: LeagueCompetition): boolean => {
                if (!competition.entry) return true;
                if (lc.entry === competition.entry) return true;
                if (competition.teamRollup && lc.entry === COMP_ENTRIES.TEAM) return true;
                return false;
              };
              const matches = leagueComps.filter(
                (lc) =>
                  entryMatches(lc) &&
                  (isMatchScoringVal(lc.scoring) && isMatchScoringVal(competition.scoring)
                    ? true
                    : lc.scoring === competition.scoring) &&
                  lc.handicap === competition.handicap
              );

              if (matches.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No matching league competitions for this scoring type.
                  </Typography>
                );
              }

              const currentId = competition.leagueCompetitionId;
              const isRollingUp = !!competition.leagueRollup;

              if (matches.length === 1) {
                const match = matches[0];
                return (
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={isRollingUp && currentId === match.itemId ? 'yes' : 'no'}
                      onChange={(_, val) => {
                        if (val === null) return;
                        const on = val === 'yes';
                        handleFieldChange('leagueRollup', on || undefined);
                        handleFieldChange('leagueCompetitionId', on ? match.itemId : undefined);
                      }}
                    >
                      <ToggleButton value="yes">Yes</ToggleButton>
                      <ToggleButton value="no">No</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="body2" color="text.secondary">
                      Count toward <strong>{match.name}</strong> league standings
                    </Typography>
                  </Stack>
                );
              }

              return (
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">League standings</Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={currentId ?? 'none'}
                    onChange={(_, val) => {
                      if (val === null) return;
                      if (val === 'none') {
                        handleFieldChange('leagueRollup', undefined);
                        handleFieldChange('leagueCompetitionId', undefined);
                      } else {
                        handleFieldChange('leagueRollup', true);
                        handleFieldChange('leagueCompetitionId', val);
                      }
                    }}
                  >
                    {matches.map((lc) => (
                      <ToggleButton key={lc.itemId} value={lc.itemId}>{lc.name}</ToggleButton>
                    ))}
                    <ToggleButton value="none">None</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              );
            })()}

            {competition.leagueRollup && (
              <TextField
                label="Bye points"
                size="small"
                type="number"
                value={competition.byePoints ?? ''}
                slotProps={{ htmlInput: { min: 0 } }}
                onChange={({ target }) =>
                  handleFieldChange('byePoints', target.value === '' ? undefined : Number(target.value))
                }
              />
            )}
          </CollapsibleSection>

          {/* ── COMBINE ── */}
          {/* Combining across days is meaningless when there's only one golf day to apply this
              game to — a social (isGolf === false) day is never a candidate, so it doesn't count
              toward "is this multi-day" here — so the whole section stays hidden. */}
          {eventDays && eventDays.filter((d) => d.isGolf !== false).length > 1 && (
          <CollapsibleSection
            sectionRef={combineRef}
            title="Composite Game"
            isOpen={openSections.has('combine')}
            onToggle={() => toggleSection('combine')}
          >
            {/* Which days this game applies to, and whether it auto-combines across its own days,
                live here alongside the explicit link picker below — both are "how this game
                combines across days," just the implicit (self) and explicit (linked) versions of
                the same question. Moved out of the Rules section, which is where this used to
                live, at the user's own request. */}
            {(() => {
              const labels = computeDayLabels(eventDays);
              // A competition can only ever apply to golf days — a social (isGolf === false) day
              // never has scores or games, so it's excluded from both "all days" and the picker
              // below. Indices into eventDays stay absolute (dayIndices addresses the full array),
              // only the offered/selectable set is narrowed.
              const golfDayIndices = eventDays.reduce<number[]>((acc, d, i) => {
                if (d.isGolf !== false) acc.push(i);
                return acc;
              }, []);
              const selectedDays = selectedDayIndices(competition, eventDays);
              const isAllDays = !selectedDays;
              // Same shape isSelfAggregateEligible checks server-side (functions/src/event/utils/
              // eventAggregateResultUtils.ts) — a single explicitly-selected day (dayIndices of
              // length 1) has nothing to aggregate across, so the toggle below only makes sense to
              // show when this game's own day scoping actually spans more than one day.
              const spansMultipleDays = isAllDays || selectedDays.length > 1;
              return (
                <Stack spacing={1} sx={{ pb: 2 }}>
                  <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Applies to</Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={isAllDays ? 'all' : 'select'}
                      onChange={(_, val) => {
                        if (val === 'all') handleDaysChange(undefined);
                        else if (val === 'select') handleDaysChange([golfDayIndices[0] ?? 0]);
                      }}
                    >
                      <ToggleButton value="all">All days</ToggleButton>
                      <ToggleButton value="select">Select days</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                  {!isAllDays && (
                    <ToggleButtonGroup
                      size="small"
                      value={(selectedDays ?? []).map(String)}
                      onChange={(_, newVals: string[]) => {
                        if (newVals.length === 0) return;
                        const indices = (newVals as string[]).map(Number).sort((a, b) => a - b);
                        handleDaysChange(indices.length === golfDayIndices.length ? undefined : indices);
                      }}
                    >
                      {golfDayIndices.map((i) => (
                        <ToggleButton key={i} value={String(i)}>{labels[i]}</ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  )}
                  {spansMultipleDays && (
                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Total across rounds</Typography>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={competition.aggregateAcrossDays === false ? 'off' : 'on'}
                        onChange={(_, val) => {
                          if (val === null) return;
                          handleFieldChange('aggregateAcrossDays', val === 'off' ? false : undefined);
                        }}
                      >
                        <ToggleButton value="on">On</ToggleButton>
                        <ToggleButton value="off">Off</ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>
                  )}
                  {/* A game linked into a composite game totals there instead (its own total
                      doesn't run), so the missed-day settings are the composite's. */}
                  {spansMultipleDays && competition.aggregateAcrossDays !== false && !competition.eventAggregateCompetitionId && (
                    <MissedDayAllowanceEditor
                      allowance={competition.missedDayAllowance}
                      isDQForEntireEvent={competition.isDQForEntireEvent}
                      dayCount={isAllDays ? golfDayIndices.length : selectedDays.length}
                      onAllowanceChange={(allowance) => handleFieldChange('missedDayAllowance', allowance)}
                      onDQForEntireEventChange={(isDQ) => handleFieldChange('isDQForEntireEvent', isDQ ? undefined : false)}
                    />
                  )}
                </Stack>
              );
            })()}

            {(() => {
              const aggComps: EventAggregateCompetition[] = eventAggregateCompetitions ?? [];
              if (aggComps.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No composite games configured for this event.
                  </Typography>
                );
              }

              // Hard gate — scoringMethod/aggregation/rankDirection must all match exactly, same
              // "denomination" reasoning as the League section's own picker above (against the
              // more precise engine fields instead of the label-based scoring+handicap match
              // League rollup uses). `entry` is exempted for the two composite shapes that credit
              // across unit shapes (see creditsFor in shared/utils/aggregateEntryUtils.ts): a TEAM
              // composite combines days of different shapes into one team total via teamId (a
              // four-ball's PAIR day and a singles SINGLE day — see
              // EventAggregateCompetition.winPointValue), and an Individual composite credits each
              // member of a pair/group unit with that unit's total (a rotating-partner event's
              // individual leaderboard, or an individual day plus a partner day). Every other
              // composite shape (PAIR/GROUP/etc) still requires an exact entry match.
              const matches = aggComps.filter((ac) =>
                (ac.entry === COMP_ENTRIES.TEAM || ac.entry === COMP_ENTRIES.SINGLE || ac.entry === competition.entry) &&
                ac.scoringMethod === competition.scoringMethod &&
                ac.aggregation === competition.aggregation &&
                ac.rankDirection === competition.rankDirection
              );

              if (matches.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No matching composite games for this configuration.
                  </Typography>
                );
              }

              const currentId = competition.eventAggregateCompetitionId;

              // Handicap mismatch — warn, don't block (deliberate product decision, not an
              // oversight). EventAggregateCompetition doesn't store handicap at all — each day's
              // own totalScore is already resolved by the time it's combined — so "mismatch" means
              // this competition's own handicap differs from an already-linked sibling's, checked
              // against siblingCompetitions rather than the aggregate itself.
              const handicapMismatch = (ac: EventAggregateCompetition): boolean =>
                (siblingCompetitions ?? []).some((m) =>
                  m.itemId !== competition.itemId &&
                  m.eventAggregateCompetitionId === ac.itemId &&
                  m.handicap !== undefined &&
                  competition.handicap !== undefined &&
                  m.handicap !== competition.handicap
                );

              const picker = matches.length === 1 ? (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={currentId === matches[0].itemId ? 'yes' : 'no'}
                    onChange={(_, val) => {
                      if (val === null) return;
                      handleFieldChange('eventAggregateCompetitionId', val === 'yes' ? matches[0].itemId : undefined);
                    }}
                  >
                    <ToggleButton value="yes">Yes</ToggleButton>
                    <ToggleButton value="no">No</ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="body2" color="text.secondary">
                    Combine into <strong>{matches[0].name}</strong>
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">Combine into</Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={currentId ?? 'none'}
                    onChange={(_, val) => {
                      if (val === null) return;
                      handleFieldChange('eventAggregateCompetitionId', val === 'none' ? undefined : val);
                    }}
                  >
                    {matches.map((ac) => (
                      <ToggleButton key={ac.itemId} value={ac.itemId}>{ac.name}</ToggleButton>
                    ))}
                    <ToggleButton value="none">None</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              );

              const selected = matches.find((ac) => ac.itemId === currentId);

              return (
                <Stack spacing={1}>
                  {picker}
                  {selected && handicapMismatch(selected) && (
                    <Typography variant="caption" sx={{ color: 'warning.main' }}>
                      This game&rsquo;s handicap basis differs from another game already combined into {selected.name} — totals will still combine, but may mix gross and net strokes.
                    </Typography>
                  )}
                </Stack>
              );
            })()}
          </CollapsibleSection>
          )}

        </Stack>

        {/* Inline create/delete actions for pages that don't use onActionsChange */}
        {!onActionsChange && (
          <Box sx={{ pt: 2 }}>
            {isNew ? (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                type="submit"
                disabled={!competition.name?.trim()}
              >
                Create
              </Button>
            ) : handleDelete ? (
              deleteConfirm ? (
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="contained" color="error" startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteRef.current?.(competitionRef.current)}>
                    Confirm delete
                  </Button>
                  <Button size="small" onClick={() => setDeleteConfirm(false)}>Cancel</Button>
                </Stack>
              ) : (
                <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
                  onClick={() => setDeleteConfirm(true)}>
                  Delete
                </Button>
              )
            ) : null}
          </Box>
        )}
      </Box>
    </Container>
  );
});

export default CompetitionEditor;
